/* STORY-002 — sign in.
 *
 * The two refusals on this screen are the interesting part, and both look like
 * bugs to anyone who does not know why:
 *
 *   · a wrong password and an unknown address give the SAME message;
 *   · being rate-limited points at the password reset, because a lockout with
 *     no way out makes a forgetful person keep trying.
 *
 * The prototype settled both. They are the rule that stops the form being used
 * to discover who has an account.
 */

import { signInRequestSchema } from '@eventuley/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useTranslate } from '../../../shared/i18n/index.js';
import { AuthScreen, Button, Callout, TextField } from '../../../shared/ui/index.js';
import { useSignIn } from '../api/index.js';

type FormValues = z.infer<typeof signInRequestSchema>;

export function SignInScreen() {
  const t = useTranslate();
  const navigate = useNavigate();
  const signIn = useSignIn();
  /* Where they were headed before they were asked to sign in. The router has
   * already refused anything that is not an in-app path, so an absolute URL
   * cannot arrive here — this screen would otherwise be an open redirect, and
   * a sign-in page is precisely the one worth phishing through. */
  const { redirect } = useSearch({ from: '/sign-in' });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(signInRequestSchema),
    mode: 'onBlur',
    defaultValues: { email: '', password: '' },
  });

  /* `mutate` with a callback, not `mutateAsync`.
   *
   * `mutateAsync` returns a promise that rejects on failure. The failure is
   * already handled — it is rendered from `signIn.error` below — but the
   * rejection is still unhandled, and the test run reported four of them. The
   * alternative is a `try/catch` whose `catch` block does nothing, which reads
   * exactly like the swallowed error `coding.md` forbids even though it is not
   * one. This says the same thing without the empty block. */
  const onSubmit = handleSubmit((values) => {
    signIn.mutate(values, {
      onSuccess: ({ user }) => {
        /* Where signing in lands — STORY-009, and all three arms are real now.
         *
         * A deep link wins over everything: somebody who followed a URL into a
         * specific screen and was asked to sign in wanted THAT screen, not a
         * chooser and not their first workspace. */
        if (redirect) {
          void navigate({ href: redirect, replace: true });
          return;
        }

        /* No chooser for a choice of one. This lived on the chooser itself
         * until STORY-009, which made the drawer's "Switch workspace" a loop
         * back to where you already were — the decision is about where SIGNING
         * IN lands, so it belongs here. */
        const only = user.memberships.length === 1 ? user.memberships[0] : undefined;
        if (only) {
          void navigate({
            to: '/orgs/$organisationId/calendar',
            params: { organisationId: only.organisationId },
          });
          return;
        }

        /* None, or several. A User with no Memberships is signed in and has
         * nowhere to go, which is the normal state of a brand-new account
         * (ADR-0010); a User with several has a choice to make. Both are the
         * same screen — STORY-004's fork and STORY-009's switcher. */
        void navigate({ to: '/next' });
      },
    });
  });

  const failure = signIn.error;
  const locked = failure instanceof ApiError && failure.code === 'rate_limited';

  return (
    <AuthScreen
      title={t('auth.sign_in.title')}
      brand={t('app.name')}
      footer={
        <>
          {t('auth.sign_in.no_account')} <Link to="/sign-up">{t('auth.sign_in.create_link')}</Link>
        </>
      }
    >
      {locked ? (
        <Callout tone="warning" title={t('auth.sign_in.locked_title')}>
          {t('auth.sign_in.locked_body')}
        </Callout>
      ) : null}

      {failure && !locked ? (
        <Callout tone="danger">
          {failure instanceof NetworkError
            ? t('error.network')
            : failure instanceof ApiError && failure.code === 'unauthenticated'
              ? /* One message for both a wrong password and an address with no
                   account. Deliberately unhelpful. */
                t('auth.sign_in.failed')
              : t('error.unexpected')}
        </Callout>
      ) : null}

      <form onSubmit={onSubmit} noValidate>
        <TextField
          label={t('auth.sign_in.email_label')}
          type="email"
          /* `username`, not `email` — that is the token password managers look
              for when pairing a field with `current-password`. */
          autoComplete="username"
          error={errors.email ? t('error.required') : undefined}
          {...register('email')}
        />

        <TextField
          label={t('auth.sign_in.password_label')}
          type="password"
          autoComplete="current-password"
          error={errors.password ? t('error.required') : undefined}
          {...register('password')}
        />

        <Button type="submit" block busy={isSubmitting || signIn.isPending}>
          {isSubmitting || signIn.isPending ? t('common.submitting') : t('auth.sign_in.submit')}
        </Button>
      </form>

      <p className="auth__alt">
        {/* A real link now — STORY-003. The lockout message above tells a
            locked-out person that resetting will let them back in sooner, and
            until this existed that copy pointed at nothing. */}
        <Link to="/forgot-password">{t('auth.sign_in.forgot')}</Link>
      </p>
    </AuthScreen>
  );
}
